import React from 'react';
import { Box, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { colors, typography } from '../constants';
import CostEditor from '../components/CostEditor';
import { useScreenLoading } from '../components/ScreenLoading';
import { ErrorView, RefreshingOverlay } from '../components/States';
import { useAnalytics } from '../hooks/useProfitData';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import { useDateRange } from '../hooks/useSelection';
import { dateRangeLabel } from '../lib/format';

/**
 * What the selected period costs, category by category. Everything here is
 * reported: the figures are entered in Settings → Costs, and the food cost is
 * calculated from the item costs, so this screen never writes anything back.
 */
export default function MyCosts() {
  const { goToTab, goToImport } = useProfitIqTabs();
  // `isFetching` so a business switch reloads behind the loader instead of
  // showing figures that no longer apply.
  const { data, isFetching, error } = useAnalytics();
  const { range, customLabel, month } = useDateRange();
  const rangeLabel = dateRangeLabel(range, month, customLabel);

  const showLoader = isFetching && !data;
  useScreenLoading('costs', showLoader ? 'Loading costs…' : null);

  return (
    <Box
      sx={{
        position: 'relative',
        pb: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <RefreshingOverlay visible={isFetching && !showLoader} />

      {error && !data ? <ErrorView message="Could not load costs." /> : null}

      {data ? (
        <>
          <Box>
            <Typography
              sx={{
                fontSize: typography.heading,
                fontWeight: 800,
                color: colors.foreground,
              }}
            >
              My Costs
            </Typography>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                backgroundColor: colors.accent,
                borderRadius: 2,
                px: 1.25,
                py: 0.625,
                mt: 1,
              }}
            >
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: 15, color: colors.primary }}
              />
              <Typography
                sx={{
                  fontSize: typography.caption,
                  fontWeight: 700,
                  color: colors.primary,
                }}
              >
                {`Showing ${rangeLabel}`}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
                mt: 0.75,
              }}
            >
              Open a category to see its line items. These figures are set in
              Settings → Costs, and Food &amp; Beverage comes from your item
              costs.
            </Typography>
          </Box>

          {data.channels.length === 0 && data.itemsTotal === 0 ? (
            <Box
              component="button"
              type="button"
              onClick={goToImport}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                backgroundColor: colors.accent,
                border: 'none',
                borderRadius: 3,
                p: 1.75,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
            >
              <CloudUploadOutlinedIcon
                sx={{ fontSize: 20, color: colors.primary }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: typography.caption,
                  color: colors.accentForeground,
                  lineHeight: 1.5,
                }}
              >
                Set your costs in Settings, or <b>import your sales data</b> to
                see profit margins update in real time.
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 20, color: colors.primary }} />
            </Box>
          ) : null}

          <CostEditor
            readOnly
            cats={data.costCats}
            totalRevenue={data.totalRevenue}
            rangeLabel={rangeLabel}
            salesTax={{
              pct: data.salesTaxPct,
              collected: data.salesTaxCollected,
              customerPaid: data.customerPaid,
              revenue: data.totalRevenue,
            }}
            onEditItemCosts={() => goToTab('menu')}
          />
        </>
      ) : null}
    </Box>
  );
}
